import React from 'react';

import { Badge, Button, Input, Panel, PanelBody, PanelHeader, Textarea } from '../../../components/ui';
import { BVStudioStatus, BVTemplateType } from '../types';
import { formatStatusLabel, formatTemplateLabel, getStatusBadgeVariant } from '../utils';

interface ContentEditorProps {
  title: string;
  templateType: BVTemplateType;
  status: BVStudioStatus;
  variants: string[];
  selectedVariantIndex: number;
  onSelectVariant: (index: number) => void;
  onTitleChange: (value: string) => void;
  onContentChange: (value: string) => void;
  canEdit?: boolean;
  isDirty?: boolean;
  isSaving?: boolean;
  onSave?: () => void;
  className?: string;
}

export function ContentEditor({
  title,
  templateType,
  status,
  variants,
  selectedVariantIndex,
  onSelectVariant,
  onTitleChange,
  onContentChange,
  canEdit = false,
  isDirty = false,
  isSaving = false,
  onSave,
  className,
}: ContentEditorProps) {
  const selectedContent = variants[selectedVariantIndex] ?? variants[0] ?? '';

  return (
    <Panel className={['flex min-h-0 flex-col overflow-hidden', className].filter(Boolean).join(' ')}>
      <PanelHeader className="shrink-0">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{formatTemplateLabel(templateType)}</Badge>
              <Badge variant={getStatusBadgeVariant(status)}>{formatStatusLabel(status)}</Badge>
              {isDirty ? <Badge variant="warning">Unsaved changes</Badge> : null}
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            </p>
          </div>
          {canEdit && onSave ? (
            <Button onClick={onSave} disabled={!isDirty || isSaving}>
              {isSaving ? 'Saving...' : 'Save draft edits'}
            </Button>
          ) : null}
        </div>
      </PanelHeader>
      <PanelBody className="flex min-h-0 flex-1 flex-col space-y-4">
        <Input
          variant="ghost-lg"
          value={title}
          onChange={event => onTitleChange(event.target.value)}
          placeholder="Untitled draft"
          disabled={!canEdit || isSaving}
          aria-label="Draft title"
        />

        <div className="flex flex-wrap gap-2">
          {variants.map((_, index) => (
            <Button
              key={index === 0 ? 'primary' : `variant-${index}`}
              variant={selectedVariantIndex === index ? 'default' : 'outline'}
              size="sm"
              onClick={() => onSelectVariant(index)}
            >
              {index === 0 ? 'Primary draft' : `Variant ${index + 1}`}
            </Button>
          ))}
        </div>

        <Textarea
          value={selectedContent}
          onChange={event => onContentChange(event.target.value)}
          readOnly={!canEdit}
          className="min-h-[320px] flex-1 text-sm leading-6 xl:min-h-0"
          aria-label="Studio draft content"
        />

        {!canEdit ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Move this item back to draft to edit the wording and save changes.
          </p>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

export default ContentEditor;
